"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

const RE_SCI =
  /^([+-]?(?:\d+\.?\d*|\.\d+))\s*[x×*]\s*10\s*(?:\^|\*\*)?\(?([+-]?\d+)\)?$/i;
const RE_E_NOTATION = /^([+-]?(?:\d+\.?\d*|\.\d+))[eE]([+-]?\d+)$/;
const RE_SIGN = /^[+-]/;
const RE_LEADING_ZEROS = /^0+(?=\d)/;
const RE_TRAILING_ZEROS = /(\.\d*?)0+$/;
const RE_TRAILING_DOT = /\.$/;
const RE_NONZERO = /[1-9]/;
const RE_TRAILING_ZERO_RUN = /0+$/;

const MAX_EXPONENT = 1000;

type Mode = "decimal" | "scientific" | "enotation";

const MODE_VALUES: Mode[] = ["decimal", "scientific", "enotation"];

/** English strings. A translated content file overrides these through `ui`. */
export const SCIENTIFIC_NOTATION_CONVERTER_STRINGS = {
  modeLabels: {
    decimal: "Decimal number → scientific",
    scientific: "Scientific (3.2 × 10^5) → decimal",
    enotation: "E-notation (3.2e5) → decimal",
  },
  whatAreYouEntering: "What are you entering?",
  yourNumber: "Your number",
  decimalPlaceholder: "320000",
  notationPlaceholder: "3.2e5 or 3.2 x 10^5",
  significantFigures: "Significant figures",
  decimalError: "Enter a plain decimal number, like 320000 or 0.00047.",
  notationParseError:
    "Could not parse that. Use a form like 3.2e5, 3.2E-4, or 3.2 x 10^5.",
  exponentTooLarge: "Exponents beyond ±{max} are not supported.",
  scientificNotationLabel: "Scientific notation",
  eNotationLabel: "E-notation",
  engineeringNotationLabel: "Engineering notation",
  engineeringNotationFullLabel:
    "Engineering notation (exponent is a multiple of 3)",
  zeroCaseValue: "0 × 10^0",
  eZeroCaseValue: "0e0",
  zeroCaseStep: "Zero is a special case: it is written 0 × 10^0.",
  decimalNumberLabel: "Decimal number",
  normalizedScientificLabel: "Normalized scientific notation",
  result: "Result",
  stepsShown: "Steps shown",
  zeroExponent:
    "{input} already has exactly one nonzero digit before the decimal point, so the exponent is 0: {mantissa} × 10^0.",
  step1:
    "one:Start with {input} and move the decimal point {count} place to the {direction}, so exactly one nonzero digit remains before it. That gives the coefficient {mantissa}.|other:Start with {input} and move the decimal point {count} places to the {direction}, so exactly one nonzero digit remains before it. That gives the coefficient {mantissa}.",
  step2Left:
    "one:You moved the point {count} place left, so the exponent is positive: the {sign} exponent is {exponent}.|other:You moved the point {count} places left, so the exponent is positive: the {sign} exponent is {exponent}.",
  step2Right:
    "one:You moved the point {count} place right, so the exponent is negative: the {sign} exponent is {exponent}.|other:You moved the point {count} places right, so the exponent is negative: the {sign} exponent is {exponent}.",
  signPositive: "positive",
  signNegative: "negative",
  resultLine: "Result: {value}.",
  notationStep1:
    "one:The exponent is {exponent}, so move the decimal point of {mantissa} {count} place to the {direction}, filling empty places with zeros.|other:The exponent is {exponent}, so move the decimal point of {mantissa} {count} places to the {direction}, filling empty places with zeros.",
  directionLeft: "left",
  directionRight: "right",
} as const;

function placePoint(digits: string, point: number): string {
  if (point <= 0) {
    return `0.${"0".repeat(-point)}${digits}`;
  }
  if (point >= digits.length) {
    return digits + "0".repeat(point - digits.length);
  }
  return `${digits.slice(0, point)}.${digits.slice(point)}`;
}

function cleanNumber(value: string): string {
  let out = value.replace(RE_LEADING_ZEROS, "");
  if (out.includes(".")) {
    out = out.replace(RE_TRAILING_ZEROS, "$1").replace(RE_TRAILING_DOT, "");
  }
  return out;
}

function shiftDecimal(mantissaStr: string, exponent: number): string {
  const negative = mantissaStr.startsWith("-");
  const unsigned = mantissaStr.replace(RE_SIGN, "");
  const [intPartRaw, fracPart = ""] = unsigned.split(".");
  const intPart = intPartRaw.length === 0 ? "0" : intPartRaw;
  const digits = intPart + fracPart;
  const point = intPart.length + exponent;
  const cleaned = cleanNumber(placePoint(digits, point));
  return negative ? `-${cleaned}` : cleaned;
}

export interface SciParts {
  mantissa: string;
  exponent: number;
}

function toScientificParts(value: number, sigFigs: number): SciParts {
  const [mantissa, expStr] = value.toExponential(sigFigs - 1).split("e");
  return { mantissa, exponent: Number.parseInt(expStr, 10) };
}

function toEngineering(value: number, sigFigs: number): SciParts {
  const { exponent } = toScientificParts(value, sigFigs);
  const engExponent = Math.floor(exponent / 3) * 3;
  const mantissa = Number.parseFloat(
    (value / 10 ** engExponent).toPrecision(sigFigs)
  );
  return { mantissa: String(mantissa), exponent: engExponent };
}

export function parseNotation(input: string): SciParts | null {
  const trimmed = input.trim();
  const sciMatch = RE_SCI.exec(trimmed);
  if (sciMatch !== null) {
    return {
      mantissa: sciMatch[1],
      exponent: Number.parseInt(sciMatch[2], 10),
    };
  }
  const eMatch = RE_E_NOTATION.exec(trimmed);
  if (eMatch !== null) {
    return { mantissa: eMatch[1], exponent: Number.parseInt(eMatch[2], 10) };
  }
  return null;
}

/** Zero stays 0 × 10^0; everything else is rewritten to one nonzero digit before the point. */
export function normalizeSciParts(parts: SciParts): SciParts {
  const negative = parts.mantissa.startsWith("-");
  const unsigned = parts.mantissa.replace(RE_SIGN, "");
  const [intPart, fracPart = ""] = unsigned.split(".");
  const hadPoint = unsigned.includes(".");
  // Digits as typed, with the position of the point after the first digit of intPart.
  const digits = intPart + fracPart;
  const firstNonzero = digits.search(RE_NONZERO);
  if (firstNonzero === -1) {
    return { mantissa: "0", exponent: 0 };
  }
  let significant = digits.slice(firstNonzero);
  if (!hadPoint) {
    // "45000e0" has no stated precision, so its trailing zeros are placeholders.
    significant = significant.replace(RE_TRAILING_ZERO_RUN, "") || "0";
  }
  const exponent = parts.exponent + intPart.length - 1 - firstNonzero;
  const rest = significant.slice(1);
  const mantissa =
    rest.length > 0 ? `${significant[0]}.${rest}` : significant[0];
  return { mantissa: negative ? `-${mantissa}` : mantissa, exponent };
}

type Strings = typeof SCIENTIFIC_NOTATION_CONVERTER_STRINGS;

function decimalSteps(
  input: string,
  parts: SciParts,
  strings: Strings,
  locale: string
): string[] {
  const places = Math.abs(parts.exponent);
  if (parts.exponent === 0) {
    return [t(strings.zeroExponent, { input, mantissa: parts.mantissa })];
  }
  const direction =
    parts.exponent > 0 ? strings.directionLeft : strings.directionRight;
  const sign = parts.exponent > 0 ? strings.signPositive : strings.signNegative;
  return [
    tPlural(strings.step1, locale, places, {
      input,
      direction,
      mantissa: parts.mantissa,
    }),
    tPlural(
      parts.exponent > 0 ? strings.step2Left : strings.step2Right,
      locale,
      places,
      { sign, exponent: parts.exponent }
    ),
    t(strings.resultLine, {
      value: `${parts.mantissa} × 10^${parts.exponent}`,
    }),
  ];
}

function notationSteps(
  parts: SciParts,
  decimal: string,
  strings: Strings,
  locale: string
): string[] {
  const places = Math.abs(parts.exponent);
  const direction =
    parts.exponent >= 0 ? strings.directionRight : strings.directionLeft;
  return [
    tPlural(strings.notationStep1, locale, places, {
      exponent: parts.exponent,
      mantissa: parts.mantissa,
      direction,
    }),
    t(strings.resultLine, { value: decimal }),
  ];
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border border-border bg-card p-4">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="break-all font-mono text-foreground text-sm">{value}</p>
    </div>
  );
}

const SIG_FIG_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function ScientificNotationConverterWidget() {
  const strings = useToolUi(SCIENTIFIC_NOTATION_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const [mode, setMode] = useState<Mode>("decimal");
  const [input, setInput] = useState("");
  const [sigFigs, setSigFigs] = useState(3);

  const trimmed = input.trim();

  let results: { label: string; value: string }[] = [];
  let steps: string[] = [];
  let error: string | null = null;

  if (trimmed.length > 0 && mode === "decimal") {
    const value = parseNumberInput(trimmed);
    if (value === null) {
      error = strings.decimalError;
    } else if (value === 0) {
      results = [
        {
          label: strings.scientificNotationLabel,
          value: strings.zeroCaseValue,
        },
        { label: strings.eNotationLabel, value: strings.eZeroCaseValue },
        {
          label: strings.engineeringNotationLabel,
          value: strings.zeroCaseValue,
        },
      ];
      steps = [strings.zeroCaseStep];
    } else {
      const sci = toScientificParts(value, sigFigs);
      const eng = toEngineering(value, sigFigs);
      results = [
        {
          label: strings.scientificNotationLabel,
          value: `${sci.mantissa} × 10^${sci.exponent}`,
        },
        {
          label: strings.eNotationLabel,
          value: `${sci.mantissa}e${sci.exponent}`,
        },
        {
          label: strings.engineeringNotationFullLabel,
          value: `${eng.mantissa} × 10^${eng.exponent}`,
        },
      ];
      steps = decimalSteps(trimmed, sci, strings, locale);
    }
  }

  if (trimmed.length > 0 && mode !== "decimal") {
    const parts = parseNotation(trimmed);
    if (parts === null) {
      error = strings.notationParseError;
    } else if (Math.abs(parts.exponent) > MAX_EXPONENT) {
      error = t(strings.exponentTooLarge, { max: MAX_EXPONENT });
    } else {
      const decimal = shiftDecimal(parts.mantissa, parts.exponent);
      const normalized = normalizeSciParts(parts);
      results = [
        { label: strings.decimalNumberLabel, value: decimal },
        {
          label: strings.normalizedScientificLabel,
          value: `${normalized.mantissa} × 10^${normalized.exponent}`,
        },
        {
          label: strings.eNotationLabel,
          value: `${normalized.mantissa}e${normalized.exponent}`,
        },
      ];
      steps = notationSteps(parts, decimal, strings, locale);
    }
  }

  const placeholder =
    mode === "decimal"
      ? strings.decimalPlaceholder
      : strings.notationPlaceholder;

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.whatAreYouEntering}
        </p>
        <div className="flex flex-wrap gap-3">
          {MODE_VALUES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m}
            >
              <input
                checked={mode === m}
                className="accent-primary"
                name="sci-mode"
                onChange={() => setMode(m)}
                type="radio"
                value={m}
              />
              {strings.modeLabels[m]}
            </label>
          ))}
        </div>
      </section>

      {/* Input */}
      <section className="space-y-3">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="sci-input"
          >
            {strings.yourNumber}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-4 font-mono text-foreground text-lg leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="sci-input"
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            type="text"
            value={input}
          />
        </div>
        {mode === "decimal" && (
          <div className="flex items-center gap-3">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="sig-figs"
            >
              {strings.significantFigures}
            </label>
            <select
              className="rounded-lg border border-border bg-card p-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="sig-figs"
              onChange={(e) => setSigFigs(Number.parseInt(e.target.value, 10))}
              value={sigFigs}
            >
              {SIG_FIG_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        )}
        {error !== null && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-destructive text-sm">
            {error}
          </p>
        )}
      </section>

      {/* Results */}
      {results.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.result}
          </h2>
          <div className="space-y-3">
            {results.map((r) => (
              <ResultRow key={r.label} label={r.label} value={r.value} />
            ))}
          </div>
        </section>
      )}

      {/* Steps */}
      {steps.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.stepsShown}
          </h2>
          <ol className="list-decimal space-y-2 rounded-lg border border-border bg-muted/30 p-6 pl-10 text-muted-foreground text-sm leading-relaxed">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}
